import { jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  ownerSchema,
  ownerSummarySchema,
  ownersPageSchema,
  sellersJsonStatusResultSchema,
  type OwnerMutationPayload,
  type OwnersFilter,
  OwnerSchainDataset,
} from './model';

interface OwnersClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const ownerMutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.union([z.void(), z.looseObject({ id: z.coerce.string().optional() })]),
);

const emptyResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.void(),
);

const schainPath = {
  [OwnerSchainDataset.Direct]: 'schain-direct-csv',
  [OwnerSchainDataset.Reseller]: 'schain-reseller-csv',
} as const;

const schainTemplatePath = {
  [OwnerSchainDataset.Direct]: 'download-template-direct',
  [OwnerSchainDataset.Reseller]: 'download-template-reseller',
} as const;

export function createOwnersClient({ config, accessToken, fetcher }: OwnersClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    list: (filter: OwnersFilter) =>
      api.request({
        path: `/api/owners?${serializeOwnersFilter(filter)}`,
        schema: ownersPageSchema,
      }),
    summary: () => api.request({ path: '/api/owners/summary', schema: ownerSummarySchema }),
    get: (id: string) =>
      api.request({
        path: `/api/owners/${encodeURIComponent(id)}`,
        schema: ownerSchema,
      }),
    create: (payload: OwnerMutationPayload) =>
      api.request({
        path: '/api/owners',
        schema: ownerMutationResponseSchema,
        init: jsonInit('POST', payload),
      }),
    update: (id: string, payload: OwnerMutationPayload) =>
      api.request({
        path: `/api/owners/${encodeURIComponent(id)}`,
        schema: ownerMutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
    refreshSellersJson: (id: string) =>
      api.request({
        path: `/api/owners/${encodeURIComponent(id)}/sellers-json/refresh`,
        schema: sellersJsonStatusResultSchema,
        init: jsonInit('POST', {}),
      }),
    downloadSchainTemplate: (dataset: OwnerSchainDataset) =>
      api.requestBlob({ path: `/api/owners/schain-csv/${schainTemplatePath[dataset]}` }),
    downloadSchain: (id: string, dataset: OwnerSchainDataset) =>
      api.requestBlob({
        path: `/api/owners/${encodeURIComponent(id)}/${schainPath[dataset]}`,
      }),
    uploadSchain: (id: string, dataset: OwnerSchainDataset, file: File) => {
      const body = new FormData();
      body.append('file', file, file.name);
      return api.request({
        path: `/api/owners/${encodeURIComponent(id)}/${schainPath[dataset]}`,
        schema: emptyResponseSchema,
        init: { method: 'POST', body },
      });
    },
  };
}

export function serializeOwnersFilter(filter: OwnersFilter) {
  return toSearchParams({
    name: filter.searchTerm,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  }).toString();
}

export type OwnersClient = ReturnType<typeof createOwnersClient>;
